// src/components/admin/BookingRequestsTab.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  updateDoc,
  getDoc,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { notifyCustomerOfAppointmentStatus } from '../../utils/bookingNotifications';
import { useSalonLocations } from '../../hooks/useSalonLocations';
import '../../styles/booking-requests.css';

// ── Week boundary helpers ──────────────────────────────────────────────────────
function getWeekBoundaries(now = new Date()) {
  const day = now.getDay(); // 0 = Sun … 6 = Sat
  const diffToMon = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMon);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { monday, sunday };
}

// ── Dynamic dropdown labels ────────────────────────────────────────────────────
function getCurrentWeekLabel() {
  const { monday, sunday } = getWeekBoundaries();
  const startDay   = monday.getDate();
  const endDay     = sunday.getDate();
  const startMonth = monday.toLocaleString('en-GB', { month: 'short' });
  const endMonth   = sunday.toLocaleString('en-GB', { month: 'short' });
  const endYear    = sunday.getFullYear();
  if (monday.getMonth() === sunday.getMonth()) {
    return `Current Week <${startDay}-${endDay} ${endMonth} ${endYear}>`;
  }
  const startYear = monday.getFullYear();
  if (startYear === endYear) {
    return `Current Week <${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}>`;
  }
  return `Current Week <${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}>`;
}

function getCurrentMonthLabel() {
  const now = new Date();
  const month = now.toLocaleString('en-GB', { month: 'long' });
  return `Current Month <${month} ${now.getFullYear()}>`;
}

function getCurrentYearLabel() {
  return `Current Year <${new Date().getFullYear()}>`;
}

// jump = { tab: 'pending'|'approved', timeFilter: 'current_week'|'subsequent_weeks'|… }
// onJumpConsumed() is called once the jump has been applied so Admin can clear it.
function BookingRequestsTab({ salonId, onPreview, locationId, onLocationChange, jump, onJumpConsumed }) {
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;
  const [appointments, setAppointments] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const [activeRequestsTab, setActiveRequestsTab] = useState('pending');
  const [requestsTimeFilter, setRequestsTimeFilter] = useState('subsequent_weeks');
  const [requestsSearchTerm, setRequestsSearchTerm] = useState('');

  const [checkInOpen, setCheckInOpen] = useState(null);
  const [checkInValue, setCheckInValue] = useState('Showed Up');

  const [showCancelReasonModal, setShowCancelReasonModal] = useState(false);
  const [cancelReasonTarget, setCancelReasonTarget] = useState(null);
  const [cancelReasonText, setCancelReasonText] = useState('');

  // Assign-staff state: { [appointmentId]: { open, selectedStaffId, availableStaff, loading } }
  const [assignStaffState, setAssignStaffState] = useState({});

  const hideStaffSelection = salonId === 'demo-clinic';

  // ── Apply jump target (set by notification modal Review click) ─────────────
  const jumpAppliedRef = useRef(null);
  useEffect(() => {
    if (!jump) return;
    const key = `${jump.tab}|${jump.timeFilter}`;
    if (jumpAppliedRef.current === key) return; // already applied this exact jump
    jumpAppliedRef.current = key;
    if (jump.tab)        setActiveRequestsTab(jump.tab);
    if (jump.timeFilter) setRequestsTimeFilter(jump.timeFilter);
    onJumpConsumed?.();
  }, [jump]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleAppointments = (locationId && appointments.length > 0)
    ? appointments.filter(a => !a.locationId || a.locationId === locationId)
    : appointments;

  const getFilteredAppointments = (status) => {
    if (!Array.isArray(visibleAppointments)) return [];

    let filtered = visibleAppointments.filter(apt => apt.status === status);

    const now = new Date();

    if (requestsTimeFilter === 'current_week') {
      // Show appointments whose SCHEDULED date falls within this calendar week (Mon–Sun)
      const { monday, sunday } = getWeekBoundaries(now);
      filtered = filtered.filter(apt => {
        const d = apt.startDateTime;
        return d && d >= monday && d <= sunday;
      });
    } else if (requestsTimeFilter === 'subsequent_weeks') {
      // Show appointments whose SCHEDULED date is after this calendar week
      const { sunday } = getWeekBoundaries(now);
      filtered = filtered.filter(apt => {
        const d = apt.startDateTime;
        return d && d > sunday;
      });
    } else {
      // Calendar-based filters — use creation date (when the request was submitted)
      filtered = filtered.filter(appointment => {
        const d = appointment.dateTimeCreated || appointment.startDateTime;
        if (!d) return false;
        if (requestsTimeFilter === 'month') {
          return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        }
        if (requestsTimeFilter === 'year') {
          return d.getFullYear() === now.getFullYear();
        }
        return false;
      });
    }

    if (requestsSearchTerm.trim()) {
      const searchLower = requestsSearchTerm.toLowerCase();
      filtered = filtered.filter(appointment => {
        const customerName = (appointment.customerName || '').toLowerCase();
        const customerEmail = (appointment.customerEmail || '').toLowerCase();
        const serviceName = (appointment.serviceName || '').toLowerCase();
        const staffName = (appointment.staffName || '').toLowerCase();

        return customerName.includes(searchLower) ||
               customerEmail.includes(searchLower) ||
               serviceName.includes(searchLower) ||
               staffName.includes(searchLower);
      });
    }

    return filtered.sort((a, b) => {
      const dateA = a.dateTimeCreated || a.startDateTime;
      const dateB = b.dateTimeCreated || b.startDateTime;
      return new Date(dateB) - new Date(dateA);
    });
  };

  const getPendingCount = () => {
    if (!Array.isArray(visibleAppointments)) return 0;
    return visibleAppointments.filter(apt => apt.status === 'pending').length;
  };

  // ── Assign-staff helpers ────────────────────────────────────────────────────

  const openAssignStaff = useCallback(async (request) => {
    const id = request.id;
    setAssignStaffState(prev => ({
      ...prev,
      [id]: { open: true, selectedStaffId: '', availableStaff: [], loading: true }
    }));

    try {
      // Determine which staff can perform the requested service/package
      const serviceId = request.serviceId;
      const packageId = request.packageId;

      let eligibleStaff = staffs.filter(s => s.role !== 'admin');

      // Filter by service capability if service data available
      if (serviceId && services.length > 0) {
        const serviceDoc = services.find(s => s.id === serviceId);
        if (serviceDoc?.staffIds?.length) {
          eligibleStaff = eligibleStaff.filter(s => serviceDoc.staffIds.includes(s.id));
        } else if (serviceDoc?.services) {
          // Some models store staff on the staff document as an array of serviceIds
          eligibleStaff = eligibleStaff.filter(s =>
            !s.services?.length || s.services.includes(serviceId)
          );
        } else {
          // Fall back: keep all staff who list this service (or have no services list)
          eligibleStaff = eligibleStaff.filter(s =>
            !s.services?.length || s.services.includes(serviceId)
          );
        }
      }

      // Check availability: exclude staff on leave or with a conflicting appointment
      const apptDate = request.appointmentDate || request.startDateTime?.toISOString?.()?.slice(0, 10);
      const apptStart = request.startTime || request.startDateTime?.toTimeString?.()?.slice(0, 5);
      const apptEnd   = request.endTime   || request.endDateTime?.toTimeString?.()?.slice(0, 5);

      const availableStaff = [];
      for (const staff of eligibleStaff) {
        // Check leave
        const leaveSnap = await getDocs(query(
          collection(db, 'staffLeave'),
          where('staffId', '==', staff.id),
          where('status', '==', 'approved')
        ));
        const onLeave = leaveSnap.docs.some(d => {
          const { startDate, endDate } = d.data();
          return apptDate >= startDate && apptDate <= endDate;
        });
        if (onLeave) continue;

        // Check conflicting appointments
        const conflictSnap = await getDocs(query(
          collection(db, 'appointments'),
          where('staffId', '==', staff.id),
          where('appointmentDate', '==', apptDate),
          where('status', 'in', ['pending', 'approved'])
        ));
        const hasConflict = conflictSnap.docs.some(d => {
          const data = d.data();
          if (d.id === id) return false; // skip self
          const existStart = data.startTime;
          const existEnd   = data.endTime;
          if (!existStart || !existEnd || !apptStart || !apptEnd) return false;
          return apptStart < existEnd && apptEnd > existStart;
        });
        if (hasConflict) continue;

        availableStaff.push(staff);
      }

      setAssignStaffState(prev => ({
        ...prev,
        [id]: { open: true, selectedStaffId: '', availableStaff, loading: false }
      }));
    } catch (err) {
      console.error('Error checking staff availability:', err);
      setAssignStaffState(prev => ({
        ...prev,
        [id]: { open: true, selectedStaffId: '', availableStaff: staffs.filter(s => s.role !== 'admin'), loading: false }
      }));
    }
  }, [staffs, services, salonId]);

  const selectAssignedStaff = (appointmentId, staffId) => {
    setAssignStaffState(prev => ({
      ...prev,
      [appointmentId]: { ...prev[appointmentId], selectedStaffId: staffId }
    }));
  };

  const cancelAssignStaff = (appointmentId) => {
    setAssignStaffState(prev => {
      const next = { ...prev };
      delete next[appointmentId];
      return next;
    });
  };

  const confirmAssignStaff = async (request) => {
    const id = request.id;
    const staffId = assignStaffState[id]?.selectedStaffId;
    if (!staffId) return;
    const staffMember = staffs.find(s => s.id === staffId);
    try {
      await updateDoc(doc(db, 'appointments', id), {
        staffId,
        staffName: staffMember?.name || '',
        updatedAt: new Date()
      });
      // Keep the panel open but mark staff as confirmed (collapse the picker)
      setAssignStaffState(prev => ({
        ...prev,
        [id]: { ...prev[id], open: false, confirmed: true, confirmedStaffId: staffId, confirmedStaffName: staffMember?.name || '' }
      }));
    } catch (err) {
      console.error('Error assigning staff:', err);
      alert('Failed to assign staff. Please try again.');
    }
  };

  // ── End assign-staff helpers ─────────────────────────────────────────────────

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        console.log('Loading staffs from main staffs collection');
        const staffsQuery = query(
          collection(db, 'staff'),
          where('salonId', '==', salonId)
        );
        const staffsSnapshot = await getDocs(staffsQuery);
        const staffsData = staffsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Loaded staffs:', staffsData);
        setStaffs(staffsData);

        try {
          const servicesQuery = collection(db, 'services');
          const servicesSnapshot = await getDocs(servicesQuery);
          const servicesData = servicesSnapshot.docs
            .filter(doc => doc.data().salonId === salonId)
            .map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setServices(servicesData);
        } catch (error) {
          console.log('Services collection not found, continuing without it');
          setServices([]);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [salonId]);

  useEffect(() => {
    if (!salonId) return;

    console.log('Setting up real-time listener for appointments');

    const appointmentsRef = collection(db, 'appointments');
    const appointmentsQuery = query(appointmentsRef, where('salonId', '==', salonId));

    const unsubscribeAppointments = onSnapshot(appointmentsQuery, (appointmentsSnapshot) => {
      const allAppointments = [];

      appointmentsSnapshot.forEach(doc => {
        const data = doc.data();
        console.log('Appointment found:', doc.id, data);

        if (data.salonId === salonId) {
          const startDateTime = data.dateTimeRequested?.toDate ?
            data.dateTimeRequested.toDate() :
            data.startDateTime?.toDate ?
            data.startDateTime.toDate() :
            new Date(data.dateTimeRequested || data.startDateTime);

          let endDateTime;
          if (startDateTime && data.serviceRequested?.duration) {
            endDateTime = new Date(startDateTime);
            endDateTime.setMinutes(endDateTime.getMinutes() + data.serviceRequested.duration);
          } else if (data.endDateTime?.toDate) {
            endDateTime = data.endDateTime.toDate();
          } else if (data.endDateTime) {
            endDateTime = new Date(data.endDateTime);
          } else {
            endDateTime = new Date(startDateTime);
            endDateTime.setMinutes(endDateTime.getMinutes() + 60);
          }

          const dateTimeCreated = data.dateTimeCreated?.toDate
            ? data.dateTimeCreated.toDate()
            : data.dateTimeCreated
            ? new Date(data.dateTimeCreated)
            : null;

          const updatedAt = data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : data.updatedAt
            ? new Date(data.updatedAt)
            : null;

          let serviceName = data.serviceName || data.serviceRequested?.name || '';
          if (!serviceName && data.serviceId && services.length > 0) {
            const service = services.find(s => s.id === data.serviceId);
            if (service) serviceName = service.name;
          }
          serviceName = serviceName || 'Unknown Service';

          allAppointments.push({
            id: doc.id,
            ...data,
            startDateTime: startDateTime,
            endDateTime: endDateTime,
            dateTimeCreated: dateTimeCreated,
            updatedAt: updatedAt,
            serviceName: serviceName,
            duration: data.serviceRequested?.duration || 60,
            price: data.serviceRequested?.price || 0
          });
        }
      });

      console.log('All appointments loaded:', allAppointments);
      setAppointments(allAppointments);
    });

    return () => unsubscribeAppointments();
  }, [salonId, services]);

  const handleAppointmentStatusChange = async (appointmentId, newStatus) => {
    try {
      console.log('Updating appointment status:', appointmentId, 'to:', newStatus);

      const appointmentRef = doc(db, 'appointments', appointmentId);
      // Read before updating so the customer notification uses the appointment's own details
      const appointmentSnap = await getDoc(appointmentRef);
      const appointmentData = appointmentSnap.exists() ? appointmentSnap.data() : null;

      await updateDoc(appointmentRef, {
        status: newStatus,
        updatedAt: new Date(),
        ...(newStatus === 'approved' && { approvedAt: new Date() })
      });

      // Tell the customer (push + e-mail, honouring the salon's campaign settings) - non-blocking
      if (appointmentData && (newStatus === 'approved' || newStatus === 'rejected')) {
        notifyCustomerOfAppointmentStatus({
          salonId,
          appointmentId,
          status: newStatus,
          customerEmail: appointmentData.customerEmail,
          customerName: appointmentData.customerName,
          serviceName: appointmentData.serviceRequested?.name || appointmentData.serviceName,
          staffName: appointmentData.staffName,
          appointmentDateTime: appointmentData.dateTimeRequested
        });
      }

      setShowRequestModal(false);
      setSelectedRequest(null);

      console.log('Appointment status updated successfully');

    } catch (error) {
      console.error('Error updating appointment status:', error);
      alert('Failed to update appointment status. Please try again.');
    }
  };

  const handleCheckIn = async (appointmentId, status) => {
    try {
      await updateDoc(doc(db, 'appointments', appointmentId), {
        checkInStatus: status,
        checkInAt: new Date(),
        updatedAt: new Date(),
      });
      setCheckInOpen(null);
      if (selectedRequest?.id === appointmentId) {
        setSelectedRequest(prev => ({ ...prev, checkInStatus: status }));
      }
    } catch (error) {
      console.error('Error updating check-in status:', error);
      alert('Failed to update check-in status. Please try again.');
    }
  };

  const isPastOrToday = (dateTime) => {
    if (!dateTime) return false;
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);
    return dateTime <= todayEnd;
  };

  const isFuture = (dateTime) => {
    if (!dateTime) return false;
    return dateTime > new Date();
  };

  const openCancelReason = (appointment, e) => {
    if (e) e.stopPropagation();
    setCancelReasonTarget(appointment);
    setCancelReasonText('');
    setShowCancelReasonModal(true);
  };

  const handleCancelWithReason = async () => {
    if (!cancelReasonText.trim()) {
      alert('Please provide a reason for cancellation.');
      return;
    }
    try {
      const appointmentRef = doc(db, 'appointments', cancelReasonTarget.id);
      await updateDoc(appointmentRef, {
        status: 'cancelled',
        cancelledAt: new Date(),
        updatedAt: new Date(),
        cancelledByAdmin: true,
        cancellationReason: cancelReasonText.trim(),
      });
      // WhatsApp cancellation notice (no push/e-mail template exists for cancellations) - non-blocking
      notifyCustomerOfAppointmentStatus({ salonId, status: 'cancelled', appointmentId: cancelReasonTarget.id });
      setShowCancelReasonModal(false);
      setCancelReasonTarget(null);
      setCancelReasonText('');
      setShowRequestModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      alert('Failed to cancel appointment. Please try again.');
    }
  };

  const formatRequestDateTime = (dateTime) => {
    if (!dateTime) return '';
    const d = dateTime instanceof Date ? dateTime : new Date(dateTime);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="booking-requests-loading">
        <div className="spinner"></div>
        <p>Loading booking requests...</p>
      </div>
    );
  }

  return (
    <div className="booking-requests-container">
      <div className="editor-page-header">
        <h2>Booking Requests</h2>
        <div className="editor-page-actions">
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
        </div>
      </div>

      {/* Mandatory location selector — shown only for multi-location salons */}
      {isMultiLocation && (
        <div className="booking-location-bar">
          <span className="booking-location-bar-label">Location:</span>
          {locations.map(loc => (
            <button
              key={loc.locationId}
              className={`booking-loc-pill${locationId === loc.locationId ? ' booking-loc-pill--active' : ''}`}
              onClick={() => onLocationChange && onLocationChange(loc.locationId)}
            >
              {loc.locationName}
            </button>
          ))}
        </div>
      )}

      <div className="requests-management-section">
        <div className="requests-header">
          <div className="requests-tabs">
            <button
              className={`requests-tab ${activeRequestsTab === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveRequestsTab('pending')}
            >
              Pending Requests
              {getPendingCount() > 0 && (
                <span className="requests-count-badge">{getPendingCount()}</span>
              )}
            </button>
            <button
              className={`requests-tab ${activeRequestsTab === 'approved' ? 'active' : ''}`}
              onClick={() => setActiveRequestsTab('approved')}
            >
              Approved Requests
            </button>
          </div>
        </div>

        <div className="requests-filters">
          <div className="time-filter">
            <label>Time Period:</label>
            <select
              value={requestsTimeFilter}
              onChange={(e) => setRequestsTimeFilter(e.target.value)}
            >
              <option value="current_week">{getCurrentWeekLabel()}</option>
              <option value="month">{getCurrentMonthLabel()}</option>
              <option value="year">{getCurrentYearLabel()}</option>
              <option value="subsequent_weeks">Subsequent Weeks</option>
            </select>
          </div>

          <div className="search-filter">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search by customer name, email, service..."
              value={requestsSearchTerm}
              onChange={(e) => setRequestsSearchTerm(e.target.value)}
              className="search-input"
            />
            {requestsSearchTerm && (
              <button
                className="clear-search"
                onClick={() => setRequestsSearchTerm('')}
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="requests-content">
          {(() => {
            const filteredRequests = getFilteredAppointments(activeRequestsTab);

            if (filteredRequests.length === 0) {
              return (
                <div className="no-requests">
                  <p>No {activeRequestsTab} requests found for the selected period.</p>
                  {requestsSearchTerm && (
                    <p>Try adjusting your search term or time filter.</p>
                  )}
                </div>
              );
            }

            return (
              <div className="requests-list">
                {filteredRequests.map(request => (
                  <div
                    key={request.id}
                    className={`request-item ${request.status}`}
                    onClick={() => {
                      setSelectedRequest(request);
                      setCheckInOpen(null);
                      setShowRequestModal(true);
                    }}
                  >
                    <div className="request-main-info">
                      <div className="request-service">
                        <h4>{request.serviceName || 'Service Request'}</h4>
                        <span className={`request-status ${request.status}`}>
                          {request.status.toUpperCase()}
                        </span>
                      </div>

                      <div className="request-details-grid">
                        <div className="request-detail">
                          <span className="label">Customer:</span>
                          <span className="value">{request.customerName || request.customerEmail || 'Customer'}</span>
                        </div>
                        <div className="request-detail">
                          <span className="label">Staff:</span>
                          <span className="value">{request.staffName}</span>
                        </div>
                        <div className="request-detail">
                          <span className="label">Date & Time:</span>
                          <span className="value">{formatRequestDateTime(request.startDateTime)}</span>
                        </div>
                        <div className="request-detail">
                          <span className="label">Duration:</span>
                          <span className="value">{request.duration} min</span>
                        </div>
                        <div className="request-detail">
                          <span className="label">Price:</span>
                          <span className="value">${request.price || 'TBD'}</span>
                        </div>
                        <div className="request-detail">
                          <span className="label">Created:</span>
                          <span className="value">{formatRequestDateTime(request.dateTimeCreated)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="request-actions">
                      {activeRequestsTab === 'pending' && (() => {
                        const aState = assignStaffState[request.id];
                        const staffAssigned = hideStaffSelection
                          ? (aState?.confirmed || request.staffId)
                          : true;
                        const confirmedName = aState?.confirmedStaffName || request.staffName;
                        return (
                          <>
                            {hideStaffSelection && (
                              <div className="assign-staff-inline" onClick={e => e.stopPropagation()}>
                                {aState?.confirmed && (
                                  <span className="assigned-staff-badge">
                                    Staff: {confirmedName}
                                  </span>
                                )}
                                {aState?.open ? (
                                  <div className="assign-staff-picker">
                                    {aState.loading ? (
                                      <span className="assign-staff-loading">Checking availability…</span>
                                    ) : (
                                      <>
                                        <select
                                          className="assign-staff-select"
                                          value={aState.selectedStaffId}
                                          onChange={e => selectAssignedStaff(request.id, e.target.value)}
                                        >
                                          <option value="">
                                            {aState.availableStaff.length === 0
                                              ? 'No staff available'
                                              : 'Select staff…'}
                                          </option>
                                          {aState.availableStaff.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}{s.title ? ` – ${s.title}` : ''}</option>
                                          ))}
                                        </select>
                                        <button
                                          className="assign-staff-confirm-btn"
                                          disabled={!aState.selectedStaffId}
                                          onClick={() => confirmAssignStaff(request)}
                                        >
                                          Confirm
                                        </button>
                                        <button
                                          className="assign-staff-cancel-btn"
                                          onClick={() => cancelAssignStaff(request.id)}
                                        >
                                          ×
                                        </button>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    className="assign-staff-btn"
                                    onClick={() => openAssignStaff(request)}
                                  >
                                    {aState?.confirmed ? 'Change Staff' : 'Assign Staff'}
                                  </button>
                                )}
                              </div>
                            )}
                            <button
                              className="quick-approve-btn"
                              disabled={!staffAssigned}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAppointmentStatusChange(request.id, 'approved');
                              }}
                            >
                              Approve
                            </button>
                            <button
                              className="quick-reject-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAppointmentStatusChange(request.id, 'rejected');
                              }}
                            >
                              Reject
                            </button>
                          </>
                        );
                      })()}

                      {isFuture(request.startDateTime) && (
                        <button
                          className="quick-cancel-btn"
                          onClick={(e) => openCancelReason(request, e)}
                        >
                          Cancel Appointment
                        </button>
                      )}

                      {activeRequestsTab === 'approved' && isPastOrToday(request.startDateTime) && (
                        checkInOpen === request.id ? (
                          <div className="checkin-dropdown" onClick={e => e.stopPropagation()}>
                            <select
                              className="checkin-select"
                              value={checkInValue}
                              onChange={e => setCheckInValue(e.target.value)}
                            >
                              <option value="Showed Up">Showed Up</option>
                              <option value="No-Show">No-Show</option>
                            </select>
                            <button
                              className="checkin-confirm-btn"
                              onClick={(e) => { e.stopPropagation(); handleCheckIn(request.id, checkInValue); }}
                            >
                              Confirm
                            </button>
                            <button
                              className="checkin-cancel-inline-btn"
                              onClick={(e) => { e.stopPropagation(); setCheckInOpen(null); }}
                            >
                              ×
                            </button>
                          </div>
                        ) : request.checkInStatus ? (
                          <div className="checkin-done-row" onClick={e => e.stopPropagation()}>
                            <span className={`checkin-status-badge checkin-status-badge--${request.checkInStatus === 'Showed Up' ? 'showed' : 'noshow'}`}>
                              {request.checkInStatus}
                            </span>
                            <button
                              className="checkin-btn checkin-btn--sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCheckInValue(request.checkInStatus);
                                setCheckInOpen(request.id);
                              }}
                            >
                              Change
                            </button>
                          </div>
                        ) : (
                          <button
                            className="checkin-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCheckInValue('Showed Up');
                              setCheckInOpen(request.id);
                            }}
                          >
                            Check In
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Cancel Reason Modal */}
      {showCancelReasonModal && cancelReasonTarget && (
        <div className="modal-overlay" onClick={() => setShowCancelReasonModal(false)}>
          <div className="booking-request-modal cancel-reason-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Appointment</h3>
              <button className="close-button" onClick={() => setShowCancelReasonModal(false)}>×</button>
            </div>
            <div className="modal-content">
              <p className="cancel-reason-intro">
                You are cancelling the appointment for <strong>{cancelReasonTarget.customerName || cancelReasonTarget.customerEmail}</strong> on <strong>{cancelReasonTarget.startDateTime ? formatRequestDateTime(cancelReasonTarget.startDateTime) : ''}</strong>.
              </p>
              <p className="cancel-reason-intro">Please provide a reason — this will be shown to the customer in their portal.</p>
              <textarea
                className="cancel-reason-textarea"
                placeholder="Enter cancellation reason..."
                value={cancelReasonText}
                onChange={e => setCancelReasonText(e.target.value)}
                rows={4}
                autoFocus
              />
              <div className="modal-actions">
                <button
                  className="cancel-button"
                  onClick={handleCancelWithReason}
                  disabled={!cancelReasonText.trim()}
                >
                  Confirm Cancellation
                </button>
                <button
                  className="approve-button"
                  style={{ backgroundColor: '#666', borderColor: '#666' }}
                  onClick={() => setShowCancelReasonModal(false)}
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Request Modal */}
      {showRequestModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => { setShowRequestModal(false); setCheckInOpen(null); }}>
          <div className="booking-request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Appointment Request Details</h3>
              <button
                className="close-button"
                onClick={() => { setShowRequestModal(false); setCheckInOpen(null); }}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="request-details">
                <div className="detail-row">
                  <label>Customer:</label>
                  <span>{selectedRequest.customerName || selectedRequest.customerEmail}</span>
                </div>
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{selectedRequest.customerEmail}</span>
                </div>
                <div className="detail-row">
                  <label>Staff:</label>
                  {hideStaffSelection ? (() => {
                    const mState = assignStaffState[selectedRequest.id];
                    const assignedName = mState?.confirmedStaffName || selectedRequest.staffName;
                    return (
                      <div className="assign-staff-modal-row">
                        <span>{assignedName || <em>Not yet assigned</em>}</span>
                        {mState?.open ? (
                          <div className="assign-staff-picker">
                            {mState.loading ? (
                              <span className="assign-staff-loading">Checking availability…</span>
                            ) : (
                              <>
                                <select
                                  className="assign-staff-select"
                                  value={mState.selectedStaffId}
                                  onChange={e => selectAssignedStaff(selectedRequest.id, e.target.value)}
                                >
                                  <option value="">
                                    {mState.availableStaff.length === 0 ? 'No staff available' : 'Select staff…'}
                                  </option>
                                  {mState.availableStaff.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}{s.title ? ` – ${s.title}` : ''}</option>
                                  ))}
                                </select>
                                <button
                                  className="assign-staff-confirm-btn"
                                  disabled={!mState.selectedStaffId}
                                  onClick={() => confirmAssignStaff(selectedRequest)}
                                >
                                  Confirm
                                </button>
                                <button
                                  className="assign-staff-cancel-btn"
                                  onClick={() => cancelAssignStaff(selectedRequest.id)}
                                >
                                  ×
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <button
                            className="assign-staff-btn"
                            onClick={() => openAssignStaff(selectedRequest)}
                          >
                            {mState?.confirmed || selectedRequest.staffId ? 'Change Staff' : 'Assign Staff'}
                          </button>
                        )}
                      </div>
                    );
                  })() : <span>{selectedRequest.staffName}</span>}
                </div>
                <div className="detail-row">
                  <label>Service:</label>
                  <span>{selectedRequest.serviceName}</span>
                </div>
                <div className="detail-row">
                  <label>Duration:</label>
                  <span>{selectedRequest.duration} minutes</span>
                </div>
                <div className="detail-row">
                  <label>Price:</label>
                  <span>${selectedRequest.price || 'TBD'}</span>
                </div>
                <div className="detail-row">
                  <label>Appointment Date:</label>
                  <span>{selectedRequest.startDateTime?.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
                <div className="detail-row">
                  <label>Appointment Time:</label>
                  <span>{selectedRequest.startDateTime?.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                </div>
                <div className="detail-row">
                  <label>End Time:</label>
                  <span>{selectedRequest.endDateTime?.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                </div>
                <div className="detail-row">
                  <label>Date Created:</label>
                  <span>{selectedRequest.dateTimeCreated?.toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status ${selectedRequest.status}`}>
                    {selectedRequest.status.toUpperCase()}
                  </span>
                </div>
                {selectedRequest.notes && (
                  <div className="detail-row">
                    <label>Notes:</label>
                    <span>{selectedRequest.notes}</span>
                  </div>
                )}
              </div>

              {selectedRequest.status === 'pending' && (() => {
                const mState = assignStaffState[selectedRequest.id];
                const staffReady = hideStaffSelection
                  ? (mState?.confirmed || !!selectedRequest.staffId)
                  : true;
                return (
                  <div className="modal-actions">
                    <button
                      className="approve-button"
                      disabled={!staffReady}
                      title={!staffReady ? 'Assign a staff member before approving' : undefined}
                      onClick={() => handleAppointmentStatusChange(selectedRequest.id, 'approved')}
                    >
                      Approve Request
                    </button>
                    <button
                      className="reject-button"
                      onClick={() => handleAppointmentStatusChange(selectedRequest.id, 'rejected')}
                    >
                      Reject Request
                    </button>
                    {isFuture(selectedRequest.startDateTime) && (
                      <button
                        className="cancel-button"
                        onClick={() => openCancelReason(selectedRequest)}
                      >
                        Cancel Appointment
                      </button>
                    )}
                  </div>
                );
              })()}

              {selectedRequest.status === 'approved' && (
                <>
                  {isPastOrToday(selectedRequest.startDateTime) && (
                    <div className="modal-checkin-section">
                      <div className="modal-checkin-label">Check-In Status</div>
                      {checkInOpen === 'modal' ? (
                        <div className="modal-checkin-form">
                          <select
                            className="checkin-select"
                            value={checkInValue}
                            onChange={e => setCheckInValue(e.target.value)}
                          >
                            <option value="Showed Up">Showed Up</option>
                            <option value="No-Show">No-Show</option>
                          </select>
                          <button
                            className="checkin-confirm-btn"
                            onClick={() => handleCheckIn(selectedRequest.id, checkInValue)}
                          >
                            Confirm
                          </button>
                          <button
                            className="checkin-cancel-inline-btn"
                            onClick={() => setCheckInOpen(null)}
                          >
                            ×
                          </button>
                        </div>
                      ) : selectedRequest.checkInStatus ? (
                        <div className="modal-checkin-row">
                          <span className={`checkin-status-badge checkin-status-badge--${selectedRequest.checkInStatus === 'Showed Up' ? 'showed' : 'noshow'}`}>
                            {selectedRequest.checkInStatus}
                          </span>
                          <button
                            className="checkin-btn checkin-btn--sm"
                            onClick={() => {
                              setCheckInValue(selectedRequest.checkInStatus);
                              setCheckInOpen('modal');
                            }}
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <div className="modal-checkin-row">
                          <span className="checkin-not-set">Not checked in yet</span>
                          <button
                            className="checkin-btn"
                            onClick={() => { setCheckInValue('Showed Up'); setCheckInOpen('modal'); }}
                          >
                            Check In
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {isFuture(selectedRequest.startDateTime) && (
                    <div className="modal-actions">
                      <button
                        className="cancel-button"
                        onClick={() => openCancelReason(selectedRequest)}
                      >
                        Cancel Appointment
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingRequestsTab;
